import { cloneElement, isValidElement, useId, type ReactElement } from 'react';

import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/shadcn/label';

/**
 * CORTEX form field primitive.
 *
 * Wraps a single control (input / select / textarea) with a label, an
 * optional hint, a required marker, and an inline error message. It
 * injects the accessibility wiring so callers don't have to: a generated
 * `id` links the `Label` to the control, and `aria-invalid` /
 * `aria-describedby` point screen readers at the hint or error.
 *
 * This is the field building block the design system calls for — every
 * non-trivial form (built on React Hook Form + Zod) renders its fields
 * through this so labels, spacing, and error display are identical
 * everywhere.
 *
 * Usage:
 *   <FormField label="Legal name" required error={errors.name?.message}>
 *     <Input {...register('name')} />
 *   </FormField>
 */
export interface FormFieldProps {
  readonly label: string;
  readonly required?: boolean;
  readonly hint?: string;
  readonly error?: string;
  /** Exactly one control element (input / select / textarea). */
  readonly children: ReactElement<{
    id?: string;
    'aria-invalid'?: boolean;
    'aria-describedby'?: string;
  }>;
  readonly className?: string;
}

export function FormField({ label, required, hint, error, children, className }: FormFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })
    : children;

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id} className="text-xs">
        {label}
        {required && (
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        )}
      </Label>
      {control}
      {hint && !error && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
